import React, { useState, useCallback, useMemo } from 'react';
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { AddOutlined, AutorenewOutlined } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

const dateOperators = [
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'notEquals' },
    { label: 'Between', value: 'between' },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Is Not Empty', value: 'isNotEmpty' },
];

const textOperators = [
    { label: 'Contains', value: 'contains' },
    { label: 'Does Not Contain', value: 'doesNotContains' },
    { label: 'Equals', value: 'equals' },
    { label: 'Not Equals', value: 'notEquals' },
    { label: 'Starts With', value: 'startsWith' },
    { label: 'Ends With', value: 'endsWith' },
    { label: 'Is Empty', value: 'isEmpty' },
    { label: 'Is Not Empty', value: 'isNotEmpty' },
];

const FIXED_WIDTH = "13.75rem";

const MultiFilter = ({ columns, onAddFilter, filters, setFilters }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAddFilter = () => {
        const validFilters = filters.filter(isRowComplete);
        onAddFilter(validFilters);
        handleClose();
    };


    const getColumnConfig = useCallback((field) =>
        columns.find(col => col.field === field), [columns]
    );

    const handleFilterChange = useCallback((index, field, value) => {
        setFilters(prevFilters => {
            const updatedFilters = [...prevFilters];

            if (field === 'column') {
                const colConfig = getColumnConfig(value);
                const ops = colConfig?.type === 'date' ? dateOperators : textOperators;

                updatedFilters[index] = {
                    column: value,
                    operator: ops[0]?.value,
                    value: colConfig?.type === 'select' ? [] : '',
                    from: null,
                    to: null
                };
            } else {
                updatedFilters[index] = {
                    ...updatedFilters[index],
                    [field]: value
                };
            }

            return updatedFilters;
        });
    }, [getColumnConfig, setFilters]);

    const getOperatorsForColumn = useCallback((column) => {
        const colConfig = getColumnConfig(column);
        return colConfig?.type === 'date' ? dateOperators : textOperators;
    }, [getColumnConfig]);

    const isRowComplete = useCallback((filter) => {
        const colConfig = getColumnConfig(filter.column);

        if (!filter.column || !filter.operator) return false;

        if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') {
            return true;
        }

        if (colConfig?.type === 'select') {
            return Array.isArray(filter.value) && filter.value.length > 0;
        }

        if (colConfig?.type === 'date') {
            if (filter.operator === 'between') {
                return Boolean(filter.from && filter.to);
            }

            if (filter.operator === 'equals' || filter.operator === 'notEquals') {
                return Boolean(filter.from);
            }

            return false;
        }

        if (typeof filter.value === 'string') {
            return filter.value.trim().length > 0;
        }

        return false;
    }, [getColumnConfig]);


    const handleAddFilterInput = () => {
        setFilters([
            ...filters,
            { column: '', operator: '', value: [], from: null, to: null }
        ]);
    };

    const handleRemoveFilter = (index) => {
        if (filters.length > 1) {
            setFilters(filters.filter((_, i) => i !== index));
        }
    };

    const handleClearFilters = () => {
        onAddFilter([{ column: '', operator: '', value: [], from: null, to: null }]);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'filter-menu' : undefined;

    const isActiveRowComplete = useMemo(
        () => filters.some(isRowComplete),
        [filters, isRowComplete]
    );

    return (
        <>
            <Button
                size="small"
                variant="text"
                onClick={handleClick}
                type="submit"
                component="label"
                sx={{
                    color: "#fff !important",
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    "& svg": {
                        color: "#fff !important"
                    }
                }}
                className="btn-theme"
            >
                <FilterAltOutlinedIcon /> Filters
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ padding: 2 }}>
                    {filters.map((filter, index) => (
                        <Box
                            key={index}
                            sx={{
                                marginBottom: 2,
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: 1,
                                padding: 1
                            }}
                        >
                            <Box sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Select
                                        value={filter.column ?? ""}
                                        onChange={(e) => handleFilterChange(index, 'column', e.target.value)}
                                        displayEmpty
                                        fullWidth
                                        size='small'
                                        sx={{ width: FIXED_WIDTH }}
                                    >
                                        <MenuItem key="" value="">
                                            Select an option
                                        </MenuItem>
                                        {columns.map((col) => (
                                            <MenuItem key={col.field} value={col.field}>
                                                {col.headerName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Select
                                        value={filter.operator}
                                        onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                                        displayEmpty
                                        fullWidth
                                        size='small'
                                        sx={{ width: FIXED_WIDTH }}
                                    >
                                        <MenuItem key="" value="">
                                            Select an option
                                        </MenuItem>
                                        {getOperatorsForColumn(filter.column).map((op) => (
                                            <MenuItem key={op.value} value={op.value}>
                                                {op.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box>
                                    {(() => {
                                        const colConfig = getColumnConfig(filter.column);

                                        if (filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty') {
                                            return null;
                                        }

                                        if (colConfig?.type === 'date') {
                                            const isBetween = filter.operator === 'between';
                                            const isSingleDate =
                                                filter.operator === 'equals' || filter.operator === 'notEquals';

                                            return (
                                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {(isBetween || isSingleDate) && (
                                                            <DatePicker
                                                                label={isBetween ? "From" : "Date"}
                                                                value={filter.from}
                                                                onChange={(newValue) =>
                                                                    handleFilterChange(index, 'from', newValue)
                                                                }
                                                                inputFormat="YYYY-MM-DD"
                                                                renderInput={(params) => (
                                                                    <TextField {...params} size="small" sx={{ width: FIXED_WIDTH }} />
                                                                )}
                                                            />
                                                        )}

                                                        {isBetween && (
                                                            <DatePicker
                                                                label="To"
                                                                value={filter.to}
                                                                onChange={(newValue) =>
                                                                    handleFilterChange(index, 'to', newValue)
                                                                }
                                                                inputFormat="YYYY-MM-DD"
                                                                renderInput={(params) => (
                                                                    <TextField {...params} size="small" sx={{ width: FIXED_WIDTH }} />
                                                                )}
                                                            />
                                                        )}
                                                    </Box>
                                                </LocalizationProvider>
                                            );
                                        }

                                        if (colConfig?.type === 'select') {
                                            return (
                                                <Select
                                                    multiple
                                                    displayEmpty
                                                    value={filter.value || []}
                                                    onChange={(e) =>
                                                        handleFilterChange(index, 'value', e.target.value)
                                                    }
                                                    size="small"
                                                    sx={{ width: FIXED_WIDTH }}
                                                    fullWidth
                                                    renderValue={(selected) => {
                                                        if (!selected || selected.length === 0) {
                                                            return <em>Select option(s)</em>;
                                                        }

                                                        return selected
                                                            .map(val =>
                                                                colConfig.options.find(opt => opt.value === val)?.label
                                                            )
                                                            .join(', ');
                                                    }}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Select option(s)</em>
                                                    </MenuItem>

                                                    {colConfig.options.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            <Checkbox checked={(filter.value || []).includes(opt.value)} />
                                                            <ListItemText primary={opt.label} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            );
                                        }

                                        return (
                                            <TextField
                                                value={filter.value}
                                                disabled={filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty'}
                                                onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                                                placeholder="Filter value"
                                                size="small"
                                                sx={{ width: FIXED_WIDTH }}
                                            />
                                        );
                                    })()}
                                </Box>
                            </Box>
                            {filters.length > 1 && (
                                <IconButton onClick={() => handleRemoveFilter(index)} sx={{ marginLeft: 1 }}>
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip
                                title={!isActiveRowComplete ? "Complete the current filter row to apply" : ""}
                                disableHoverListener={isActiveRowComplete}
                            >
                                <span>
                                    <Button
                                        onClick={handleAddFilterInput}
                                        variant="outlined"
                                        disabled={!isActiveRowComplete}
                                    >
                                        <AddOutlined />
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button onClick={handleClearFilters} variant="outlined">
                                <AutorenewOutlined />
                            </Button>
                        </Box>
                        <Box sx ={{marginLeft: "0.5rem"}}>
                            <Tooltip
                                title={!isActiveRowComplete ? "Complete the current filter row to apply" : ""}
                                disableHoverListener={isActiveRowComplete}
                            >
                                <span>
                                    <Button
                                        disabled={!isActiveRowComplete}
                                        variant="contained"
                                        onClick={handleAddFilter}
                                    >
                                        Apply Filters
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default MultiFilter;